/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    ViewChild,
} from '@angular/core';
import { BaseComponent } from '../base.component';
import { LoadingService, LogService } from '../../services';
import { takeUntil } from 'rxjs/operators';
import { ErrorResponseModel } from '../../models';

@Component({
    selector: 'app-split-action-button',
    templateUrl: './split-action-button.component.html',
    styleUrls: ['./split-action-button.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class SplitActionButtonComponent extends BaseComponent implements AfterViewInit {
    /**
     * Reference to the button wrapper, so we can get its size.
     */
    @ViewChild('contentWrapper', { static: true })
    public contentWrapper: ElementRef;

    /**
     * Set the button ID of the split button.
     */
    @Input() public buttonId: string;

    /**
     * Set the button class of the split button.
     */
    @Input() public buttonClass: string = 'primary';

    /**
     * Set the button text of the embedded loading button.
     */
    @Input() public buttonText: string = 'click';

    /**
     * Set the button icon of the embedded loading button.
     */
    @Input() public buttonIconLocation: string = null;

    /**
     * Set the button state of the split button.
     */
    @Input() public buttonDisabled: boolean = false;

    /**
     * Set default width for split button content container.
     */
    @Input() public contentWidth: number = 250;

    /**
     * Pass the callback to the loading button
     */
    @Input() public callBack: (...args: any[]) => void;

    /**
     * Loading State.
     */
    public isLoading: boolean = false;

    /**
     * Store the width of the button for the loading state.
     */
    public splitButtonWidth: number = 0;

    constructor(
        private cdr: ChangeDetectorRef,
        private loadingService: LoadingService,
        private logService: LogService,
    ) {
        super();

        this.subscribeToLoadingServices();
    }

    public ngAfterViewInit(): void {
        this.splitButtonWidth = this.contentWrapper.nativeElement.clientWidth + 8; // <- Action dots padding
    }

    /**
     * Subscribe to loading services to clear either the action dots or the button click loader.
     * @private
     */
    private subscribeToLoadingServices(): void {
        this.loadingService.loadingButtonEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    if (typeof (response) === 'boolean') {
                        this.setState(response);
                    } else {
                        const isLoading = response.id === this.buttonId ? response.state : false;
                        this.setState(isLoading);
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'SplitActionButtonComponent:loadingButtonEmitter',
                        error.message
                    );
                }
            });

        this.loadingService.loadingActionDotsEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (state: boolean): void => {
                    this.setState(state);
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error(
                        'SplitActionButtonComponent:ActionDotsEmitter',
                        error.message
                    );
                }
            });
    }

    /**
     * Set state helper function.
     * @param state
     * @private
     */
    private setState(state: any): void {
        // This setTimeout is because there is a timing issue with the subscriber completing
        // before the loading state is set in the split button component.
        const timerId = setTimeout(() => {
            if (typeof state === 'boolean') {
                this.isLoading = state;
            } else {
                this.isLoading = state.id === this.buttonId ? state.state : false;
            }
            this.cdr.detectChanges();
            clearTimeout(timerId);
        }, 500);
    }
}
